import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { exchangeService, type Exchange, type TimelineResponse, type MessageResponse } from '@/services/exchange'
import { useAuthStore } from '@/stores/auth'

/**
 * Exchange Store - Single-Thread Architecture
 * 
 * Much simpler than conversation store - no conversation management needed!
 * Just manages user's continuous exchange timeline and current session.
 */
export const useExchangeStore = defineStore('exchange', () => {
  // ================================
  // CORE STATE - Much Simpler!
  // ================================
  
  // User's exchange timeline (latest first)
  const exchanges = ref<Exchange[]>([])
  
  // Loading and processing states
  const isProcessing = ref(false) // Sending message
  const isLoading = ref(false)    // Loading timeline
  const error = ref<string | null>(null)
  
  
  // Timeline pagination
  const totalExchanges = ref(0)
  const hasMoreExchanges = ref(false)
  const currentOffset = ref(0)
  const pageSize = ref(20)
  
  // Session management
  const currentProjectId = ref<string | undefined>(undefined)
  const isSessionActive = ref(false)

  // ================================
  // COMPUTED VALUES
  // ================================
  
  const latestExchange = computed(() => exchanges.value[0]) // Latest first
  const hasExchanges = computed(() => exchanges.value.length > 0)
  
  // Processing status of latest exchange
  const isLatestExchangeProcessing = computed(() => {
    if (!latestExchange.value) return false
    const status = latestExchange.value.processing_status
    return status === 'pending' || status === 'processing' || status === 'transcribing'
  })

  // ================================
  // ACTIONS - Much Simpler!
  // ================================

  /**
   * Send text message to user's thread
   * No conversation ID needed - direct to user's thread!
   */
  const sendMessage = async (
    message: string, 
    projectId?: string, 
    options: Record<string, any> = {}
  ): Promise<MessageResponse> => {
    isProcessing.value = true
    error.value = null
    
    try {
      console.log('[ExchangeStore] Sending message to single-thread:', {
        messageLength: message.length,
        projectId,
        hasOptions: Object.keys(options).length > 0
      })

      const result = await exchangeService.sendMessage(message, projectId, options)

      // Timeline will be updated via WebSocket notifications
      console.log('[ExchangeStore] Message sent, awaiting WebSocket updates:', {
        exchange_id: result.exchange_id,
        message: result.message
      })
      
      return result
    } catch (err: any) {
      error.value = err.message
      console.error('[ExchangeStore] Failed to send message:', err)
      throw err
    } finally {
      isProcessing.value = false
    }
  }

  /**
   * Send audio message to user's thread
   * Handles multipart upload seamlessly
   */
  const sendAudioMessage = async (
    audioBlob: Blob, 
    projectId?: string,
    options: Record<string, any> = {}
  ): Promise<MessageResponse> => {
    isProcessing.value = true
    error.value = null
    
    try {
      console.log('[ExchangeStore] Sending audio message:', {
        audioSize: audioBlob.size,
        audioType: audioBlob.type,
        projectId
      })

      const result = await exchangeService.sendAudioMessage(audioBlob, projectId, options)

      // Timeline will be updated via WebSocket notifications
      console.log('[ExchangeStore] Audio message sent, awaiting WebSocket updates:', {
        exchange_id: result.exchange_id,
        message: result.message
      })
      
      return result
    } catch (err: any) {
      error.value = err.message
      console.error('[ExchangeStore] Failed to send audio message:', err)
      throw err
    } finally {
      isProcessing.value = false
    }
  }

  /**
   * Load user's exchange timeline
   * Replaces complex conversation loading logic
   */
  const loadTimeline = async (
    limit = 20, 
    offset = 0, 
    refresh = false,
    includeWorkflows = false
  ): Promise<TimelineResponse> => {
    if (refresh) {
      currentOffset.value = 0
      offset = 0
    }
    
    isLoading.value = true
    error.value = null
    
    try {
      console.log('[ExchangeStore] Loading timeline:', { limit, offset, refresh })

      const response = await exchangeService.getTimeline(limit, offset, includeWorkflows)
      
      console.log('[ExchangeStore] Timeline API response:', {
        dataLength: response.data?.length || 0,
        metaTotal: response.meta?.total || 0,
        hasData: Array.isArray(response.data)
      })
      
      if (refresh || offset === 0) {
        // Replace timeline (fresh load)
        exchanges.value = response.data || []
      } else {
        // Append to timeline (load more)
        if (Array.isArray(response.data)) {
          exchanges.value.push(...response.data)
        }
      }
      
      // Update pagination metadata
      totalExchanges.value = response.meta?.total || 0
      hasMoreExchanges.value = response.meta?.has_more || false
      currentOffset.value = offset + (response.data?.length || 0)
      pageSize.value = limit
      
      console.log('[ExchangeStore] Timeline loaded successfully:', {
        totalLoaded: exchanges.value.length,
        totalAvailable: totalExchanges.value,
        hasMore: hasMoreExchanges.value,
        hasExchanges: hasExchanges.value
      })
      
      return response
    } catch (err: any) {
      error.value = err.message
      console.error('[ExchangeStore] Failed to load timeline:', err)
      console.error('[ExchangeStore] Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data
      })
      
      // Don't throw - let initialization continue with empty state
      console.warn('[ExchangeStore] Continuing with empty timeline due to error')
      return {
        data: [],
        meta: { total: 0, per_page: limit, current_offset: offset, has_more: false },
        user_thread_info: { total_exchanges: 0 }
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Load more exchanges (infinite scroll)
   */
  const loadMore = async (): Promise<void> => {
    if (hasMoreExchanges.value && !isLoading.value) {
      await loadTimeline(pageSize.value, currentOffset.value, false)
    }
  }


  /**
   * Refresh session information
   */
  const refreshSession = async (): Promise<void> => {
    try {
      console.log('[ExchangeStore] Refreshing session info...')
      const sessionInfo = await exchangeService.getCurrentSession()
      
      isSessionActive.value = sessionInfo.session_active
      currentProjectId.value = sessionInfo.current_project_id
      
      console.log('[ExchangeStore] Session refreshed:', {
        isActive: isSessionActive.value,
        projectId: currentProjectId.value
      })
    } catch (err: any) {
      console.error('[ExchangeStore] Failed to refresh session:', err)
      // Don't throw - this is supplementary data
    }
  }

  /**
   * Initialize store - much simpler than conversation store!
   */
  const initialize = async (): Promise<void> => {
    try {
      console.log('[ExchangeStore] Initializing single-thread store...')

      // Clear any existing state first
      reset()

      // Load timeline
      console.log('[ExchangeStore] Loading timeline...')
      await loadTimeline(pageSize.value, 0, true)
      console.log('[ExchangeStore] Timeline loaded, exchanges count:', exchanges.value.length)

      // Load session info
      await refreshSession()

      // APPROACH 2: Connect to WebSocket for real-time updates
      const authStore = useAuthStore()
      if (authStore.user?.id) {
        console.log('[ExchangeStore] Connecting to WebSocket for user:', authStore.user.id)
        connectToRealTimeUpdates(authStore.user.id)
      } else {
        console.warn('[ExchangeStore] Cannot connect to WebSocket - user not authenticated')
      }

      console.log('[ExchangeStore] Store initialized successfully', {
        totalExchanges: exchanges.value.length,
        hasExchanges: hasExchanges.value
      })
    } catch (err: any) {
      error.value = err.message
      console.error('[ExchangeStore] Failed to initialize store:', err)

      // Don't throw - allow partial initialization
      console.warn('[ExchangeStore] Continuing with partial initialization')
    }
  }

  /**
   * Clear error state
   */
  const clearError = (): void => {
    error.value = null
  }

  /**
   * Reset store to initial state
   */
  const reset = (): void => {
    exchanges.value = []
    isProcessing.value = false
    isLoading.value = false
    error.value = null
    totalExchanges.value = 0
    hasMoreExchanges.value = false
    currentOffset.value = 0
    pageSize.value = 20
    currentProjectId.value = undefined
    isSessionActive.value = false
  }

  // ================================
  // UTILITY METHODS
  // ================================

  /**
   * Find exchange by ID
   */
  const findExchange = (id: string): Exchange | undefined => {
    return exchanges.value.find(exchange => exchange.id === id)
  }


  /**
   * Get exchanges by processing status
   */
  const getExchangesByStatus = (status: Exchange['processing_status']) => {
    return exchanges.value.filter(exchange => exchange.processing_status === status)
  }

  /**
   * Connect to real-time updates via WebSocket
   * Subscribes to user's private channel for exchange notifications
   */
  const connectToRealTimeUpdates = (userId: string) => {
    if (typeof window === 'undefined' || !(window as any).Echo) {
      console.warn('[ExchangeStore] Laravel Echo not available for real-time updates')
      return
    }

    console.log(`[ExchangeStore] Connecting to real-time updates for user: ${userId}`)

    // Subscribe to user's private channel
    // Use .notification() to handle Laravel broadcast notifications
    ;(window as any).Echo.private(`user.${userId}`)
      .notification((notification: any) => {
        console.log('[ExchangeStore] Received WebSocket notification:', notification)

        // Handle ProcessingStatusNotification
        if (notification.type === 'ProcessingStatusNotification') {
          const { exchange_id, status, message, timestamp, transcription, new_exchange } = notification

          console.log('[ExchangeStore] Processing notification:', {
            exchange_id,
            status,
            message,
            has_transcription: !!transcription,
            has_new_exchange: !!new_exchange
          })

          // Handle welcome_exchange FIRST (before checking if exchange exists)
          if (status === 'welcome_exchange') {
            console.log('[ExchangeStore] Welcome exchange received, adding to timeline:', {
              exchange_id: new_exchange.id
            })

            const exists = findExchange(new_exchange.id)
            if (!exists) {
              exchanges.value.unshift(new_exchange)
              totalExchanges.value++
              console.log('[ExchangeStore] Welcome exchange added')
            }
            return
          }

          // Find the exchange in our timeline
          const exchange = findExchange(exchange_id)

          if (exchange) {
            // APPROACH 2: Handle transcription_complete with immediate data
            if (status === 'transcription_complete' && transcription) {
              console.log('[ExchangeStore] Transcription data received, updating UI immediately:', {
                exchange_id,
                text_length: transcription.text?.length || 0,
                confidence: transcription.confidence
              })

              // Ensure content object exists
              if (!exchange.content) {
                exchange.content = {}
              }

              // Update answer_content on top level (for ChatTab UI compatibility)
              exchange.answer_content = {
                message: transcription.text,
                metadata: {
                  is_from_transcription: true,
                  transcription_confidence: transcription.confidence,
                  transcription_language: transcription.language
                }
              }

              // Update answer with transcribed text
              exchange.content.answer = transcription.text

              // Update answer_dto with transcription metadata
              exchange.content.answer_dto = {
                message: transcription.text,
                has_text: true,
                has_selections: false,
                is_from_transcription: true,
                transcription_confidence: transcription.confidence,
                transcription_language: transcription.language
              }
            }

            // APPROACH 2: Handle timeline_update with full exchange data
            if (status === 'timeline_update' && new_exchange) {
              console.log('[ExchangeStore] New exchange received via WebSocket, adding directly:', {
                new_exchange_id: new_exchange.id,
                has_question: !!new_exchange.question_content
              })

              // Check if already exists (prevent duplicates)
              const exists = findExchange(new_exchange.id)
              if (!exists) {
                exchanges.value.unshift(new_exchange)
                totalExchanges.value++
                console.log('[ExchangeStore] New exchange added to timeline')
              } else {
                console.warn('[ExchangeStore] Exchange already exists, skipping:', new_exchange.id)
              }
              return
            }

            // FALLBACK: Handle timeline_update without data (old approach)
            if (status === 'timeline_update' && !new_exchange) {
              console.warn('[ExchangeStore] Timeline update without exchange data, falling back to API call')
              loadTimeline(pageSize.value, 0, true)
              return
            }

            // Update processing status for all statuses
            exchange.processing_status = status
            exchange.processing = {
              status,
              message,
              timestamp
            }
          } else {
            console.log(`[ExchangeStore] Exchange ${exchange_id} not found in timeline`)

            // If this is a new exchange (timeline_update), check if we have data
            if (status === 'timeline_update') {
              if (new_exchange) {
                console.log('[ExchangeStore] New exchange received for unknown exchange_id, adding it:', new_exchange.id)
                exchanges.value.unshift(new_exchange)
                totalExchanges.value++
              } else {
                console.log('[ExchangeStore] Timeline update without data, refreshing timeline')
                loadTimeline(pageSize.value, 0, true)
              }
            }
          }
        }
      })

    console.log(`[ExchangeStore] Successfully connected to real-time updates for user: ${userId}`)
  }

  /**
   * Disconnect from real-time updates
   */
  const disconnectFromRealTimeUpdates = (userId: string) => {
    if ((window as any).Echo) {
      console.log(`[ExchangeStore] Disconnecting from real-time updates for user: ${userId}`)
      ;(window as any).Echo.leave(`user.${userId}`)
    }
  }

  /**
   * Retry failed audio message from buffer
   */
  const retryFailedAudioMessage = async (recordingId: string): Promise<boolean> => {
    try {
      console.log('[ExchangeStore] Retrying failed audio message:', recordingId)
      
      // Get the audio blob from buffer manager
      const { audioBufferManager } = await import('@/services/audioBuffer')
      const recording = audioBufferManager.getRecording(recordingId)
      const blob = recording?.blob
      
      if (!blob) {
        throw new Error('Recording not found in buffer')
      }

      // Send the audio using exchange store
      await sendAudioMessage(blob)
      
      // Clear the failed recording from buffer
      audioBufferManager.clearRecording(recordingId)
      
      console.log('[ExchangeStore] Successfully retried audio message:', recordingId)
      return true
    } catch (error: any) {
      console.error('[ExchangeStore] Failed to retry audio message:', error)
      return false
    }
  }

  // ================================
  // RETURN STORE INTERFACE
  // ================================
  return {
    // State
    exchanges,
    isProcessing,
    isLoading,
    error,
    totalExchanges,
    hasMoreExchanges,
    currentOffset,
    pageSize,
    currentProjectId,
    isSessionActive,

    // Computed
    latestExchange,
    hasExchanges,
    isLatestExchangeProcessing,

    // Actions
    sendMessage,
    sendAudioMessage,
    loadTimeline,
    loadMore,
    initialize,
    clearError,
    reset,
    refreshSession,
    retryFailedAudioMessage,

    // Real-time updates
    connectToRealTimeUpdates,
    disconnectFromRealTimeUpdates,

    // Utilities
    findExchange,
    getExchangesByStatus
  }
})

// Export type for use in components
export type ExchangeStore = ReturnType<typeof useExchangeStore>